import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TrainingService } from '../services';
import type { PaginationInfo } from '../types';
import type { TrainingItem } from '../types/training';

export interface TrainingFilters {
  searchTerm?: string;
  groups?: string[];
}

interface TrainingGroupOption {
  value: string;
  label: string;
}

interface UseTrainingsResult {
  trainings: TrainingItem[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  totalTrainings: number;
  groupOptions: TrainingGroupOption[];
  refreshTrainings: () => Promise<void>;
}

const useTrainings = (page: number, pageSize: number, filters: TrainingFilters): UseTrainingsResult => {
  const [trainings, setTrainings] = useState<TrainingItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [groupOptions, setGroupOptions] = useState<TrainingGroupOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  const refreshTrainings = useCallback(async () => {
    setReloadKey((currentKey) => currentKey + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadFilterOptions = async () => {
      try {
        const groups = await TrainingService.getTrainingGroupOptions();

        if (!isMounted) {
          return;
        }

        setGroupOptions(groups.map((group) => ({ value: String(group.id), label: group.name })));
      } catch (serviceError) {
        if (!isMounted) {
          return;
        }

        setGroupOptions([]);
        setError(serviceError instanceof Error ? serviceError.message : 'Não foi possível carregar os treinos.');
      }
    };

    void loadFilterOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const normalizedSearch = filters.searchTerm?.trim() ?? '';
  const normalizedGroups = useMemo(() => filters.groups ?? [], [filters.groups]);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    const debounceHandle = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await TrainingService.listPaginated(page, pageSize, {
          searchTerm: normalizedSearch,
          groupIds: normalizedGroups,
        });

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setTrainings(response.data);
        setPagination(response.pagination);
        setError(null);
      } catch (serviceError) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setTrainings([]);
        setPagination((currentPagination) => ({
          ...currentPagination,
          page,
          pageSize,
          totalRecords: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        }));
        setError(serviceError instanceof Error ? serviceError.message : 'Não foi possível carregar os treinos.');
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(debounceHandle);
  }, [normalizedGroups, normalizedSearch, page, pageSize, reloadKey]);

  return {
    trainings,
    pagination,
    loading,
    error,
    totalTrainings: pagination.totalRecords,
    groupOptions,
    refreshTrainings,
  };
};

export default useTrainings;